import { useFormContext, useFormField } from '@calendly/form';
import { ArrowChevronDownIcon, LocationPinIcon, PhoneIcon, VideoIcon } from '@calendly/ui/components/icons';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { MANAGED_EVENT_TYPE } from 'constants/event_types';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { getCurrentOrganizer } from '../../utils';
import { LocationField, LocationFieldLabel } from '../location_field';
import { nonWebConferencingKinds as defaultNonWebConferencingKinds, webConferencingKinds } from './constants';
import { SquareAllOptionsButton, SquareLocationButton } from './location_buttons_and_options.js';
import { FormFieldLabel } from '../location_field/form_field_label';
import { OrganizerIntegrationError } from './organizer/integration_error';
import { OrganizerSection } from './organizer/organizer_dropdown';
const containerIsPhoneCss = "c1hgz6nk";
const containerCss = "cxwyu0j";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledLocationFieldCss = "s1vyeyoe";
const StyledLocationField = forwardRef((props, ref) => <LocationField ref={ref} {...props} className={classNames(styledLocationFieldCss, props.className)} />);
const getMembershipIntegrationStatus = (currentOrganizer, locationKind) => {
  if (!currentOrganizer) {
    return false;
  }
  const INTEGRATIONS = {
    zoom_conference: currentOrganizer.is_zoom_configured,
    gotomeeting_conference: currentOrganizer.is_gotomeeting_configured,
    microsoft_teams_conference: currentOrganizer.is_microsoft_teams_configured,
    webex_conference: currentOrganizer.is_webex_configured,
    google_conference: currentOrganizer.google_conferencing_configured
  };
  return INTEGRATIONS[locationKind] === false;
};
const LocationSelector = ({
  eventType,
  isManaged,
  potentialKind,
  clearPotentialKind,
  onLocationConfigurationChange,
  useUpdatedStyles = false,
  hideLabel = false
}) => {
  const {
    isAdhoc,
    isPoll,
    isTeam
  } = eventType;
  const {
    isCollective,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const {
    setFieldValue,
    setFieldTouched,
    values: {
      memberships = [],
      userPools,
      locationConfigurations
    }
  } = useFormContext();
  const showOrganizerDropdown = isCollective || isCollectiveRoundRobin && userPools.some(pool => pool.pooling_type === 'collective');
  const isNewEventType = !eventType?.id || isAdhoc;
  const enabledHosts = useMemo(() => isPoll ? memberships : memberships.filter(({
    enabled,
    _destroy
  }) => enabled && !_destroy), [isPoll, memberships]);
  const filteredLocations = locationConfigurations.filter(location => !(location._destroy === true));
  const locationKind = filteredLocations?.[0]?.kind || potentialKind;
  const allowMultiple = eventType.kind_name === 'one_on_one' || eventType.type === MANAGED_EVENT_TYPE && eventType.kind === 'solo';
  const organizerMemberships = useMemo(() => {
    if (isNewEventType && isCollective) {
      return enabledHosts;
    }
    if (isTeam) {
      return enabledHosts;
    }
    if (isCollectiveRoundRobin && userPools.some(pool => pool.pooling_type === 'collective')) {
      const collectiveUserPool = userPools.find(pool => pool.pooling_type === 'collective');
      const userPoolUuid = collectiveUserPool.uuid || collectiveUserPool.key;
      return enabledHosts.filter(membership => membership.user_pool_uid === userPoolUuid);
    }
    return memberships;
  }, [enabledHosts, isCollective, isCollectiveRoundRobin, isNewEventType, isTeam, memberships, userPools]);
  const automaticOrganizer = ['google_conference', 'microsoft_teams_conference'].includes(locationKind);
  const currentOrganizer = isPoll && !automaticOrganizer ? enabledHosts.find(h => h.location_organizer) : getCurrentOrganizer(organizerMemberships, locationKind, eventType.expected_calendar_pusher_membership_id);
  const pendingOrganizer = automaticOrganizer && isNewEventType;
  const organizerHasIntegrationError = getMembershipIntegrationStatus(currentOrganizer, locationKind);
  const handleChange = useCallback(newValue => {
    const locations = newValue.filter(location => !(location._destroy === true));
    if (locations.length === 0) {
      clearPotentialKind();
    }
    setFieldValue('locationConfigurations', newValue);
    setFieldTouched('locationConfigurations');
    if (onLocationConfigurationChange) {
      onLocationConfigurationChange(locations);
    }
  }, [setFieldValue, setFieldTouched, onLocationConfigurationChange, clearPotentialKind]);
  const isOrganizableConferenceLocation = ['zoom_conference', 'google_conference', 'microsoft_teams_conference', 'webex_conference', 'gotomeeting_conference'].includes(locationKind);
  const isSameMembership = useCallback((membership1, membership2) => {
    if (isAdhoc) return membership1.user_id === membership2.user_id;
    return !!membership1.organization_membership_id && membership1.organization_membership_id === membership2.organization_membership_id || !!membership1.organization_invitation_id && membership1.organization_invitation_id === membership2.organization_invitation_id;
  }, [isAdhoc]);
  const updateOrganizer = useCallback(membership => {
    const updatedMemberships = memberships.map(m => ({
      ...m,
      location_organizer: isSameMembership(m, membership)
    }));
    setFieldValue('memberships', updatedMemberships);
  }, [memberships, isSameMembership, setFieldValue]);
  useEffect(() => {
    const canHaveLocationOrganizer = isOrganizableConferenceLocation && !['google_conference', 'microsoft_teams_conference'].includes(locationKind);
    const hasMemberships = organizerMemberships && organizerMemberships.length;
    if (canHaveLocationOrganizer && hasMemberships && !organizerMemberships.some(membership => membership.location_organizer)) {
      updateOrganizer(organizerMemberships[0]);
    }
  }, [isOrganizableConferenceLocation, locationKind, organizerMemberships, updateOrganizer]);
  return <div>
      <StyledLocationField eventType={eventType} isManaged={isManaged} data-testid="location-field" onChange={handleChange} locationConfigurations={locationConfigurations} conferencingOrganizer={currentOrganizer} allowMultiple={allowMultiple} potentialKind={potentialKind} useUpdatedStyles={useUpdatedStyles} hideLabel={hideLabel} />
      {showOrganizerDropdown && isOrganizableConferenceLocation && <OrganizerSection memberships={organizerMemberships} currentOrganizer={currentOrganizer} validLocationKind={locationKind} onSelect={updateOrganizer} pendingOrganizer={pendingOrganizer} />}
      {showOrganizerDropdown && organizerHasIntegrationError && <OrganizerIntegrationError kind={locationKind} organizerName={currentOrganizer.name} />}
    </div>;
};
export const LocationFieldWithButtons = ({
  eventType,
  onLocationConfigurationChange,
  nonWebConferencingKinds = defaultNonWebConferencingKinds,
  useUpdatedStyles = false,
  hideLabel = false,
  isManaged = false,
  useNewETE = false
}) => {
  const [{
    value: locationConfigurations
  }] = useFormField({
    name: 'locationConfigurations'
  });
  const [selectedKind, updateSelectedKind] = useState(null);
  const {
    isGroup,
    isPoll
  } = eventType;
  const shouldDisableOutbound = isGroup || isPoll;
  const phoneKind = shouldDisableOutbound ? 'inbound_call' : 'outbound_call';
  const showLocationSelector = locationConfigurations.filter(lc => !(lc._destroy === true)).length > 0 || selectedKind || useNewETE;
  if (showLocationSelector) {
    return <LocationSelector eventType={eventType} potentialKind={selectedKind} useUpdatedStyles={useUpdatedStyles} clearPotentialKind={() => updateSelectedKind(null)} onLocationConfigurationChange={onLocationConfigurationChange} hideLabel={hideLabel} isManaged={isManaged} />;
  }
  return <div>
      {!hideLabel && (useUpdatedStyles ? <FormFieldLabel /> : <LocationFieldLabel />)}
      <Container data-calendly-label="event-details-location">
        <SquareLocationButton icon={<VideoIcon size="large" />} kind="zoom_conference" onClickAction={() => updateSelectedKind('zoom_conference')} />
        <SquareLocationButton icon={<PhoneIcon size="large" />} kind={phoneKind} onClickAction={() => updateSelectedKind(phoneKind)} />
        <SquareLocationButton icon={<LocationPinIcon size="large" />} kind="physical" onClickAction={() => updateSelectedKind('physical')} />
        <SquareAllOptionsButton icon={<ArrowChevronDownIcon size="large" />} kind="all_options" onSelect={updateSelectedKind} webConferencingKinds={webConferencingKinds} nonWebConferencingKinds={nonWebConferencingKinds} />
      </Container>
    </div>;
};

require("./location_field_with_buttons.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./location_field_with_buttons.js");